/* eslint-disable react/prop-types */
import { useEffect, useState, useCallback } from "react";

import Card from "@mui/material/Card";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import { CompanyService } from "services/companyService";
import { GeneralJournalService } from "services/general-journal";
import { formatDate, formatVndIntegerValue } from "utils";
import toast from "react-hot-toast";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import DataTable from "./DataTable";
import ActionCell from "./components/ActionCell/index";

const currentYear = String(new Date().getFullYear());

const tableData = {
  columns: [
    { Header: "Ngày ghi sổ", accessor: "recordDate", align: "center" },
    { Header: "Số CT", accessor: "voucherNumber", align: "center" },
    { Header: "Ngày CT", accessor: "voucherDate", align: "center" },
    { Header: "Diễn giải", accessor: "description" },
    { Header: "Chi từ TK", accessor: "trackedAccount", align: "center" },
    { Header: "TK Nợ", accessor: "counterpartAccounts", align: "center" },
    { Header: "Số tiền", accessor: "debitAmount", align: "right" },
    { Header: "Thao tác", accessor: "action", sorted: false, align: "center" },
  ],
  rows: [],
};

function JournalVoucher2() {
  const [companyId, setCompanyId] = useState("");
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [dataTableState, setDataTableState] = useState(tableData);
  const [summary, setSummary] = useState({
    totalRows: 0,
    totalDebitAmount: 0,
    totalOtherAccountAmount: 0,
    postedToLedgerCount: 0,
  });
  const [filters, setFilters] = useState({
    year: currentYear,
    fromDate: "",
    toDate: "",
    accountCode: "",
    searchBy: "",
  });
  const [query, setQuery] = useState({
    year: currentYear,
    fromDate: "",
    toDate: "",
    accountCode: "",
    searchBy: "",
    sortBy: "recordDate",
    sortType: "asc",
  });
  const [paginationData, setPaginationData] = useState({ page: 1, count: 0, size: 10 });

  useEffect(() => {
    const init = async () => {
      try {
        const response = await CompanyService.getByAuth();
        const payload = response?.data || response || {};
        setCompanyId(payload?._id || "");
      } catch (error) {
        console.error("Lỗi lấy thông tin công ty:", error);
      }
    };
    init();
  }, []);

  const fetchAccounts = useCallback(async () => {
    if (!companyId || !filters.year) return;
    try {
      setAccountsLoading(true);
      const response = await GeneralJournalService.getCashPaymentJournalAccounts({
        company: companyId,
        year: Number(filters.year),
      });
      const payload = response?.data || response || {};
      const items = (payload.items || []).filter(acc => acc.accountCode.startsWith("112"));
      setAccounts(items);

      if (items.length > 0 && !filters.accountCode) {
        const firstAccount = items[0].accountCode;
        setFilters((prev) => ({ ...prev, accountCode: firstAccount }));
        setQuery((prev) => ({ ...prev, accountCode: firstAccount }));
      }
    } catch (error) {
      setAccounts([]);
    } finally {
      setAccountsLoading(false);
    }
  }, [companyId, filters.year, filters.accountCode]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const fetchData = useCallback(async () => {
    if (!companyId || !query.year || !query.accountCode) {
      setDataTableState((prev) => ({ ...prev, rows: [] }));
      setSummary({ totalRows: 0, totalDebitAmount: 0, totalOtherAccountAmount: 0, postedToLedgerCount: 0 });
      setPaginationData((prev) => ({ ...prev, count: 0 }));
      return;
    }

    try {
      setLoading(true);
      const response = await GeneralJournalService.getCashPaymentJournal({
        company: companyId,
        year: Number(query.year),
        accountCode: query.accountCode,
        fromDate: query.fromDate || undefined,
        toDate: query.toDate || undefined,
        searchBy: query.searchBy || undefined,
        page: paginationData.page,
        limit: paginationData.size,
      });

      const payload = response?.data || response || {};
      const trackedAccount = payload.trackedAccount || {};
      setSummary(
        payload.summary || {
          totalRows: 0,
          totalDebitAmount: 0,
          totalOtherAccountAmount: 0,
          postedToLedgerCount: 0,
        }
      );
      setPaginationData((prev) => ({
        ...prev,
        count: payload.paginate?.count || 0,
        size: payload.paginate?.size || prev.size,
      }));

      const rows = (payload.items || []).map((item) => {
        const counterpartCodes = [
          ...(payload.detailColumns || []).filter((column) => Number(item?.detailAmounts?.[column.key] || 0) > 0).map((column) => column.accountCode),
          ...(item?.otherAccountCodes || []),
        ].join(", ");

        const params = new URLSearchParams({
          year: String(query.year || currentYear),
          fromDate: String(query.fromDate || ""),
          toDate: String(query.toDate || ""),
          accountCode: String(query.accountCode || ""),
          searchBy: String(query.searchBy || ""),
        }).toString();

        return {
          recordDate: formatDate(item.recordDate),
          voucherNumber: item.voucherNumber || "",
          voucherDate: formatDate(item.voucherDate),
          description: item.description || "",
          trackedAccount: trackedAccount?.accountCode || query.accountCode,
          counterpartAccounts: counterpartCodes || "",
          debitAmount: formatVndIntegerValue(item.debitAmount || 0),
          action: <ActionCell item={{ viewLink: `/templates/journal-voucher-2/view?${params}` }} />,
        };
      });

      setDataTableState((prev) => ({ ...prev, rows }));
    } catch (error) {
      toast.error(error?.response?.data?.message || "Không thể tải Nhật ký Chứng từ số 2");
      setDataTableState((prev) => ({ ...prev, rows: [] }));
      setSummary({ totalRows: 0, totalDebitAmount: 0, totalOtherAccountAmount: 0, postedToLedgerCount: 0 });
      setPaginationData((prev) => ({ ...prev, count: 0 }));
    } finally {
      setLoading(false);
    }
  }, [companyId, query, paginationData.page, paginationData.size]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSync = async () => {
    if (!companyId || !filters.year) {
      toast.error("Thiếu thông tin công ty hoặc năm");
      return;
    }
    try {
      setSyncing(true);
      await GeneralJournalService.syncAppendix1({ company: companyId, year: Number(filters.year) });
      toast.success("Đồng bộ dữ liệu thành công");
      await fetchAccounts();
      setPaginationData((prev) => ({ ...prev, page: 1 }));
      setQuery((prev) => ({ ...prev, ...filters }));
      await fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Đồng bộ dữ liệu thất bại");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox my={3}>
        <Card style={{ height: "100%" }}>
          <DataTable
            table={dataTableState}
            loading={loading}
            syncing={syncing}
            filters={filters}
            setFilters={setFilters}
            accounts={accounts}
            accountsLoading={accountsLoading}
            query={query}
            setQuery={setQuery}
            paginationData={paginationData}
            setPaginationData={setPaginationData}
            onSync={handleSync}
            onFetchData={(newFilters) => {
              setQuery((prev) => ({ ...prev, ...newFilters }));
              setPaginationData((prev) => ({ ...prev, page: 1 }));
            }}
          />
          <SoftBox px={3} pb={3} display="flex" justifyContent="space-between" flexWrap="wrap" gap={2}>
            <SoftBox display="flex" flexDirection="column">
              <SoftTypography variant="caption" color="text">
                Tổng số dòng: {summary.totalRows} - Đã ghi Sổ Cái: {summary.postedToLedgerCount}
              </SoftTypography>
            </SoftBox>
            <SoftBox display="flex" flexDirection="column" textAlign="right">
              <SoftTypography variant="caption" color="text">
                Tổng chi: {formatVndIntegerValue(summary.totalDebitAmount || 0)}
              </SoftTypography>
            </SoftBox>
          </SoftBox>
        </Card>
      </SoftBox>
    </DashboardLayout>
  );
}

export default JournalVoucher2;
